import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { farmDateTime, type EstimateItem, type MapContext, type MapMarker } from '../../api/demo-client.js';

/**
 * Fachada do mapa.
 *
 * Toda a dependência de Leaflet fica aqui: a página só entrega dados e recebe
 * seleção. O que esta classe garante:
 *
 * - marcador SÓ para item com coordenada estimada capturada nesta fazenda —
 *   nada de centro de pasto, GPS do leitor ou poste como posição de animal;
 * - um único símbolo para todos (M03-R3): ponto pequeno com contorno escuro,
 *   sem raio de incerteza. Só a cor interna muda, pela idade da última leitura
 *   no instante exibido (`readAgeBand`); a seleção acrescenta só um aro e o
 *   tooltip (hover/foco) resume etiqueta, pasto, leitura e situação;
 * - um marcador por item mapeável, sem agrupamento;
 * - enquadramento automático só na PRIMEIRA carga da fazenda; atualizações
 *   posteriores não recentralizam o mapa do usuário;
 * - desenho só com o marcador mínimo (M03-FP); etiqueta e pastos chegam
 *   depois em `setDetails` e só trocam título/tooltip, sem recriar marcador;
 * - mapa-base ausente ou com erro de tile vira aviso explícito, e pastos e
 *   marcadores continuam desenhados (vetores não dependem de tile).
 */

export interface TileConfig {
  readonly url: string | null;
  readonly attribution: string | null;
  readonly maxNativeZoom: number;
  readonly maxZoom: number;
}

/**
 * Mapa-base padrão: tiles de satélite do Google servidos ao Leaflet, o mesmo
 * padrão já usado no Landwatch. Sem chave, proxy ou SDK. O provedor entrega
 * tiles até 19; o Leaflet permite aproximar até 22 ampliando o último tile.
 */
export const DEFAULT_TILES: TileConfig = {
  url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
  attribution: 'Google',
  maxNativeZoom: 19,
  maxZoom: 22,
};

/**
 * Configuração vinda do ambiente Vite. Variável ausente usa o satélite padrão;
 * `VITE_MAP_TILE_URL` sobrescreve o provedor (a atribuição padrão não vale para
 * outro provedor) e `VITE_MAP_TILE_ATTRIBUTION` sobrescreve a atribuição. URL
 * explicitamente vazia desliga o mapa-base, como já fazia a fachada.
 */
export function tileConfig(env: { VITE_MAP_TILE_URL?: string; VITE_MAP_TILE_ATTRIBUTION?: string }): TileConfig {
  if (env.VITE_MAP_TILE_URL === undefined) {
    return { ...DEFAULT_TILES, attribution: env.VITE_MAP_TILE_ATTRIBUTION ?? DEFAULT_TILES.attribution };
  }
  return {
    url: env.VITE_MAP_TILE_URL,
    attribution: env.VITE_MAP_TILE_ATTRIBUTION ?? null,
    maxNativeZoom: 20,
    maxZoom: 22,
  };
}

/** Classe única do marcador de animal; a seleção só acrescenta o aro. */
export const ANIMAL_MARKER_CLASS = 'animal-marker';
export const ANIMAL_MARKER_SELECTED_CLASS = 'animal-marker--selected';

const DAY_MS = 24 * 60 * 60 * 1000;

export type ReadAgeBand = 'recent' | 'one_day' | 'two_days' | 'never';

/**
 * Idade da última leitura no instante exibido, sem arredondar. Leitura
 * "futura" (pequena discrepância de relógio) conta como idade zero.
 */
export function readAgeBand(lastReadAtMs: number | null, referenceAtMs: number): ReadAgeBand {
  if (lastReadAtMs === null) return 'never';
  const age = Math.max(0, referenceAtMs - lastReadAtMs);
  if (age < DAY_MS) return 'recent';
  if (age < 2 * DAY_MS) return 'one_day';
  return 'two_days';
}

export const READ_AGE_CLASS: Record<ReadAgeBand, string> = {
  recent: 'animal-marker--recent',
  one_day: 'animal-marker--one-day',
  two_days: 'animal-marker--two-days',
  never: 'animal-marker--never',
};

export const READ_AGE_LABEL: Record<ReadAgeBand, string> = {
  recent: 'Lido há menos de 1 dia',
  one_day: 'Sem leitura há mais de 1 dia',
  two_days: 'Sem leitura há 2 dias ou mais',
  never: 'Nunca lido',
};

/** Ícones compartilhados por faixa: sem HTML, sem dado do item — só classes CSS. */
function icon(band: ReadAgeBand, selected: boolean): L.DivIcon {
  const classes = [ANIMAL_MARKER_CLASS, READ_AGE_CLASS[band], ...(selected ? [ANIMAL_MARKER_SELECTED_CLASS] : [])];
  return L.divIcon({ className: classes.join(' '), html: '', iconSize: [12, 12] });
}
const ICONS = Object.fromEntries(
  (Object.keys(READ_AGE_CLASS) as ReadAgeBand[]).map((band) => [band, [icon(band, false), icon(band, true)]]),
) as Record<ReadAgeBand, [L.DivIcon, L.DivIcon]>;

/** Metadados que enriquecem marcador; ausentes até o endpoint completo responder. */
export type MarkerDetails = Pick<EstimateItem, 'epc' | 'pastureName' | 'expectedPastureName'>;

function markerTitle(details: MarkerDetails | undefined): string {
  return details === undefined ? 'Animal' : `Etiqueta ${details.epc}`;
}

/**
 * Conteúdo do tooltip só com `textContent`: EPC e nomes de pasto nunca viram
 * HTML. Sem metadados, só o que o marcador já sabe: última leitura e situação.
 */
function tooltipContent(marker: MapMarker, band: ReadAgeBand, details: MarkerDetails | undefined): HTMLElement {
  const read = [
    `Última leitura: ${marker.lastReadAtMs === null ? 'Nunca lida' : farmDateTime(marker.lastReadAtMs)}`,
    `Situação: ${READ_AGE_LABEL[band]}`,
  ];
  const lines = details === undefined ? read : [`Etiqueta ${details.epc}`, `Pasto: ${details.pastureName ?? '—'}`, ...read];
  if (details !== undefined && details.expectedPastureName !== null && details.expectedPastureName !== details.pastureName) {
    lines.push(`Pasto esperado: ${details.expectedPastureName}`);
  }
  const root = document.createElement('div');
  for (const line of lines) {
    const row = document.createElement('div');
    row.textContent = line;
    root.appendChild(row);
  }
  return root;
}

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** O mapa desta fazenda só desenha posição estimada capturada NESTA fazenda. */
export function mappable(item: MapMarker, farmId: string): boolean {
  return (
    (item.positionState === 'valid' || item.positionState === 'last_known') &&
    item.latitude !== null &&
    item.longitude !== null &&
    item.captureFarmId === farmId
  );
}

export class MapView {
  private readonly map: L.Map;
  private readonly overlays = L.layerGroup();
  private readonly markers = new Map<string, L.Marker>();
  private fittedFarm: string | null = null;
  private selected: string | null = null;
  private details: ReadonlyMap<string, MarkerDetails> | null = null;

  constructor(
    container: HTMLElement,
    tiles: TileConfig,
    private readonly onSelect: (targetKey: string) => void,
    private readonly onBaseLayer: (available: boolean) => void,
  ) {
    const animate = !reducedMotion();
    // Mantém vetores já desenhados ao arrastar; o padrão recorta perto da tela
    // e só inclui a nova área quando o movimento termina.
    const vectorRenderer = L.svg({ padding: 1 });
    this.map = L.map(container, {
      renderer: vectorRenderer,
      zoomControl: true,
      keyboard: true,
      attributionControl: true,
      zoomAnimation: animate,
      fadeAnimation: animate,
      markerZoomAnimation: animate,
      zoomSnap: 0,
      zoomDelta: 0.25,
      wheelDebounceTime: 10,
      wheelPxPerZoomLevel: 120,
    });
    this.map.setView([-15.79, -47.88], 4);
    this.overlays.addTo(this.map);
    if (tiles.url === null || tiles.url === '') {
      this.onBaseLayer(false);
    } else {
      const layer = L.tileLayer(tiles.url, {
        attribution: tiles.attribution ?? '',
        maxNativeZoom: tiles.maxNativeZoom,
        maxZoom: tiles.maxZoom,
      });
      let failed = false;
      layer.on('tileerror', () => {
        if (!failed) {
          failed = true;
          this.onBaseLayer(false);
        }
      });
      layer.on('tileload', () => {
        if (failed) {
          failed = false;
          this.onBaseLayer(true);
        }
      });
      layer.addTo(this.map);
      this.onBaseLayer(true);
    }
  }

  /** `referenceAtMs` é o instante consultado (`resolvedAsOfMs`), nunca o relógio local. */
  render(
    farmId: string,
    context: MapContext,
    items: readonly MapMarker[],
    selected: string | null,
    referenceAtMs: number,
    details: ReadonlyMap<string, MarkerDetails> | null = null,
  ): {
    pastures: number;
    markers: number;
  } {
    this.clear();
    this.details = details;
    const bounds = L.latLngBounds([]);

    for (const pasture of context.pastures) {
      const ring = pasture.vertices.map((vertex) => L.latLng(vertex.latitude, vertex.longitude));
      L.polygon(ring, {
        color: '#f8fafc', weight: 2, opacity: 0.9, fillColor: '#16a34a', fillOpacity: 0.08,
        className: 'pasture-shape', bubblingMouseEvents: false,
      })
        .bindTooltip(pasture.name, { sticky: true })
        .addTo(this.overlays);
      ring.forEach((point) => bounds.extend(point));
    }
    for (const pole of context.poles) {
      // Postes discretos na visão padrão; o código aparece só na interação.
      L.circleMarker([pole.latitude, pole.longitude], {
        radius: 2.5, color: '#e2e8f0', weight: 1, fillColor: '#64748b', fillOpacity: 0.9,
      })
        .bindTooltip(`Poste ${pole.epc ?? 'sem etiqueta'}`)
        .addTo(this.overlays);
    }

    let markers = 0;
    for (const item of items) {
      if (!mappable(item, farmId)) continue;
      const point = L.latLng(item.latitude!, item.longitude!);
      const band = readAgeBand(item.lastReadAtMs, referenceAtMs);
      const marker = L.marker(point, {
        keyboard: true,
        // `title` vira atributo pelo DOM (texto, não HTML): nome acessível.
        title: markerTitle(this.details?.get(item.targetKey)),
        icon: ICONS[band][item.targetKey === selected ? 1 : 0],
        riseOnHover: true,
      });
      // Ligado antes do `addTo`: o Leaflet abre o tooltip também no foco por teclado.
      // Função: cada abertura lê os metadados vigentes.
      marker.bindTooltip(() => tooltipContent(item, band, this.details?.get(item.targetKey)), {
        direction: 'top',
        offset: [0, -8],
      });
      marker.on('click', () => this.onSelect(item.targetKey));
      marker.addTo(this.overlays);
      this.markers.set(item.targetKey, marker);
      bounds.extend(point);
      markers += 1;
    }

    if (this.fittedFarm !== farmId && bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 18 });
      this.fittedFarm = farmId;
    }
    this.selected = selected;
    return { pastures: context.pastures.length, markers };
  }

  /**
   * Metadados chegaram (ou mudaram): só título e tooltip aberto são
   * atualizados. Marcadores, zoom e enquadramento ficam como estão.
   */
  setDetails(details: ReadonlyMap<string, MarkerDetails> | null): void {
    this.details = details;
    for (const [key, marker] of this.markers) {
      const title = markerTitle(details?.get(key));
      marker.options.title = title;
      marker.getElement()?.setAttribute('title', title);
      if (marker.isTooltipOpen()) marker.getTooltip()?.update();
    }
  }

  /** Troca de fazenda: nada da anterior fica desenhado. */
  clear(): void {
    this.overlays.clearLayers();
    this.markers.clear();
  }

  /** Seleção vinda da lista: destaca e mostra o marcador sem recentralizar à força. */
  select(targetKey: string | null): void {
    this.selected = targetKey;
    for (const [key, marker] of this.markers) {
      marker.getElement()?.classList.toggle(ANIMAL_MARKER_SELECTED_CLASS, key === targetKey);
    }
    const marker = targetKey === null ? undefined : this.markers.get(targetKey);
    if (marker !== undefined && !this.map.getBounds().contains(marker.getLatLng())) {
      this.map.panTo(marker.getLatLng(), { animate: !reducedMotion() });
    }
  }

  selectedKey(): string | null {
    return this.selected;
  }

  destroy(): void {
    this.map.remove();
  }
}
